import React from "react";
import { Paper, Typography, LinearProgress, Chip, Box } from "@mui/material";
import { useTranslation } from "react-i18next";

interface ProgressCardProps {
  currentProgress: number;
  currentMessage: string;
  status: string;
  researchMode?: string;
  searchCount?: number;
  maxSearches?: number;
  currentQuery?: string;
}

export const ProgressCard: React.FC<ProgressCardProps> = ({
  currentProgress,
  currentMessage,
  status,
  researchMode,
  searchCount,
  maxSearches,
  currentQuery,
}) => {
  const { t } = useTranslation();

  // 完了状態の場合は表示しない（条件を修正）
  if (status === "completed") return null;

  // メッセージから不要な情報をフィルタリング
  const getCleanMessage = (message: string): string => {
    // 翻訳キーを実際の翻訳に変換
    if (message.includes("deepResearch.process.understandingQuery") || message.includes("understandingQuery")) {
      return t("deepResearch.progress.collectingInfo");
    }
    if (message.includes("deepResearch.process.searchQuery")) {
      if (currentQuery) {
        return t("deepResearch.process.searchQuery", { query: currentQuery });
      } else {
        return t("deepResearch.progress.collectingInfo");
      }
    }
    if (message.includes("deepResearch.process.generatingFinalReport")) {
      return t("deepResearch.process.generatingFinalReport");
    }
    if (message.includes("deepResearch.process.buildingPrompt")) {
      return t("deepResearch.process.buildingPrompt");
    }
    if (message.includes("deepResearch.process.generatingReport")) {
      return t("deepResearch.process.generatingReport");
    }
    if (message.includes("deepResearch.process.researchModeStarting")) {
      return t("deepResearch.process.researchModeStarting");
    }
    if (message.includes("medium") || message.includes("high") || message.includes("low")) {
      return t("deepResearch.progress.collectingInfo");
    }

    // 既に翻訳されたメッセージかチェック
    if (message.startsWith("deepResearch.")) {
      return t(message);
    }

    return message;
  };

  const cleanMessage = getCleanMessage(currentMessage);

  // 調査モードの表示テキスト
  const getModeDisplayText = (mode?: string): string => {
    switch (mode) {
      case "detailed":
        return t("deepResearch.researchMode.detailed");
      case "fast":
        return t("deepResearch.researchMode.fast");
      default:
        return "";
    }
  };

  const modeDisplayText = getModeDisplayText(researchMode);

  // 進捗値を0以上に調整
  const displayProgress = Math.max(0, currentProgress);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h6">{t("deepResearch.progress.researchProgress")}</Typography>
        {modeDisplayText && (
          <Chip
            label={modeDisplayText}
            size="small"
            color={researchMode === "detailed" ? "primary" : "secondary"}
            variant="outlined"
          />
        )}
      </Box>

      <LinearProgress variant="determinate" value={displayProgress} sx={{ height: 8, borderRadius: 4, mb: 1 }} />

      <Typography variant="body2" color="text.secondary">
        {t("deepResearch.progress.progressCompleted", { progress: displayProgress })}
        {searchCount &&
          maxSearches &&
          ` (${t("deepResearch.progress.searchProgress", { current: searchCount, total: maxSearches })})`}
      </Typography>

      {cleanMessage && (
        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
          {cleanMessage}
        </Typography>
      )}

      {/* 詳細モードの説明 */}
      {researchMode === "detailed" && displayProgress < 100 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {t("deepResearch.researchMode.detailedExplanation")}
        </Typography>
      )}
    </Paper>
  );
};
